import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, MapPin, Mail, ChevronRight } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { GlassCard } from "@/components/erp";
import { Rozet } from "@/components/ortak/Liste";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";

const Satir = ({ l, children, testid }) => (
  <div className="flex items-center justify-between gap-4 py-3 border-b border-white/10 text-sm">
    <span className="text-muted-foreground shrink-0">{l}</span><span className="text-right font-medium truncate" data-testid={testid}>{children || "—"}</span>
  </div>
);

const AnahtarKart = ({ baslik, aciklama, checked, onChange, testid }) => (
  <GlassCard className="p-5 flex items-start gap-4">
    <div className="flex-1"><div className="font-semibold text-sm">{baslik}</div><p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{aciklama}</p></div>
    <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
  </GlassCard>
);

export const SirketBilgileri = ({ onDuzenle }) => {
  const { ayar, loadAyar } = useAuth();
  const [ek, setEk] = useState({ bitis: "", ed: null });
  useEffect(() => {
    Promise.all([api.get("/abonelik").catch(() => ({ data: { gecmis: [] } })), api.get("/edonusum/durum").catch(() => ({ data: null }))])
      .then(([a, e]) => setEk({ bitis: a.data.gecmis[0]?.bitis || "", ed: e.data }));
  }, []);
  const anahtar = async (k, v) => { try { await api.put("/ayarlar", { [k]: v }); await loadAyar(); toast.success("Kaydedildi"); } catch (e) { toast.error(errMsg(e)); } };
  const a = ayar || {};
  const ed = ek.ed;
  return (
    <div data-testid="sirket-bilgileri">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h2 className="font-head text-xl font-bold uppercase" data-testid="sirket-unvan">{a.unvan || a.kisa_ad}</h2>
        <div className="ml-auto flex gap-2">
          <button data-testid="sirket-adres-duzenle" onClick={() => onDuzenle("adres")} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg text-sm border border-white/20 hover:bg-white/5"><MapPin className="w-4 h-4" />Adresi Düzenle</button>
          <button data-testid="sirket-duzenle" onClick={() => onDuzenle("firma")} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-500 text-white"><Pencil className="w-4 h-4" />Şirketi Düzenle</button>
        </div>
      </div>
      <div className="grid lg:grid-cols-3 gap-x-10 mb-8">
        <div>
          <Satir l="Para birimi">{a.para_birimi || "TRY"}</Satir><Satir l="Telefon" testid="sirket-telefon">{a.telefon}</Satir><Satir l="E-Posta">{a.email}</Satir>
          <Satir l="İnternet Sitesi">{a.web_sitesi}</Satir><Satir l="Sektör">{a.sektor_adi && <Rozet ton="mavi">{a.sektor_adi}</Rozet>}</Satir>
          <Satir l="Logo">{a.logo_url && <img src={a.logo_url} alt="logo" className="h-8 inline-block rounded bg-white p-0.5" />}</Satir>
        </div>
        <div>
          <Satir l="Oluşturma Tarihi">{(a.created_at || "").slice(0, 10)}</Satir><Satir l="Abonelik Bitiş Tarihi" testid="sirket-abonelik-bitis">{ek.bitis}</Satir>
          <Satir l="Ülke">{a.ulke}</Satir><Satir l="Şehir">{[a.sehir, a.ilce].filter(Boolean).join(" / ")}</Satir><Satir l="Adres">{a.adres}</Satir><Satir l="Şirket Türü">{a.sirket_turu}</Satir>
        </div>
        <div>
          <Satir l="VKN / TCKN">{a.vergi_no}</Satir><Satir l="Vergi Dairesi">{a.vergi_dairesi}</Satir><Satir l="MERSİS Numarası">{a.mersis_no}</Satir>
          <Satir l="E-Fatura Entegratörü" testid="sirket-entegrator">{ed && <span className="inline-flex items-center gap-2">QNB<Rozet ton={ed.aktif ? "yesil" : "gri"}>{ed.aktif ? (ed.mod === "test" ? "Test" : "Tanımlı") : "Pasif"}</Rozet>
            <button data-testid="sirket-entegrator-duzenle" onClick={() => onDuzenle("edonusum")} className="text-[var(--g1)]"><Pencil className="w-3.5 h-3.5" /></button></span>}</Satir>
          <Satir l="Fatura Tipi">{ed?.aktif ? "e-Fatura / e-Arşiv" : "Kağıt / elle numara"}</Satir><Satir l="İşletme Merkezi">{a.isletme_merkezi}</Satir><Satir l="Ticaret Sicil No">{a.ticaret_sicil_no}</Satir>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <AnahtarKart testid="sirket-lot-skt" baslik="Lot / SKT Takibi Zorunlu" checked={!!a.lot_skt_zorunlu} onChange={(v) => anahtar("lot_skt_zorunlu", v)}
          aciklama="Açık olduğunda stok girişlerinde lot numarası ve son kullanma tarihi istenir (gıda ve kimya sektörleri için)." />
        <Link to="/ayarlar/e-posta" data-testid="sirket-eposta-link" className="glass rounded-2xl p-5 flex items-start gap-4 hover:-translate-y-0.5 transition-transform">
          <Mail className="w-5 h-5 text-[var(--g1)] mt-0.5" />
          <div className="flex-1"><div className="font-semibold text-sm">Gün Sonu Yönetici Özeti ve Diğer E-Postalar</div>
            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">Her kullanıcı hangi e-postaları alacağını kendisi seçer. Şirket geneli alıcı listesi de bu bölümdedir.</p></div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </Link>
      </div>
    </div>
  );
};
